import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ConditionGroup, Condition } from '../condition.model';
import { ConditionType, ConditionRelation } from '../../../../../../../api/src/creative-unit-package/models/visibility-condition.dto'

@Component({
	selector: 'app-recursive-condition-group',
	templateUrl: './recursive-condition-group.component.html',
	styleUrls: ['./recursive-condition-group.component.scss']
})
export class RecursiveConditionGroupComponent {
	@Input() group: ConditionGroup; // Accepts a group object as input
	@Input() parentGroup: ConditionGroup;
	@Input() mergeTagState: { [key: string]: any };

	@Output() setRootGroup: EventEmitter<ConditionGroup> = new EventEmitter();
	@Output() onChange: EventEmitter<any> = new EventEmitter();

	public collapse = false;
	public groupOperatorOptions = [
		{ label: $localize`:Visibility Condition Group Operator AND@@VisibilityConditionGroupOperatorAND:AND`, value: ConditionType.AND },
		{ label: $localize`:Visibility Condition Group Operator OR@@VisibilityConditionGroupOperatorOR:OR`, value: ConditionType.OR },
	]
	public relationOptions = [
		{ label: $localize`:Visibility Condition Relation Equals@@VisibilityConditionRelationEquals:Equals`, value: ConditionRelation.EQ },
		{ label: $localize`:Visibility Condition Relation Not Equals@@VisibilityConditionRelationNotEquals:Does Not Equal`, value: ConditionRelation.NEQ },
		{ label: $localize`:Visibility Condition Relation Greater Than@@VisibilityConditionRelationGreaterThan:Is Greater Than`, value: ConditionRelation.GT },
		{ label: $localize`:Visibility Condition Relation Greater Than And Equal@@VisibilityConditionRelationGreaterThanAndEqual:Is Greater Than Or Equal To`, value: ConditionRelation.GTE },
		{ label: $localize`:Visibility Condition Relation Lower Than@@VisibilityConditionRelationLowerThan:Is Lower Than`, value: ConditionRelation.LT },
		{ label: $localize`:Visibility Condition Relation Lower Than And Equal@@VisibilityConditionRelationLowerThanAndEqual:Is Lower Than Or Equal To`, value: ConditionRelation.LTE },
		{ label: $localize`:Visibility Condition Relation Includes@@VisibilityConditionRelationIncludes:Includes`, value: ConditionRelation.INCLUDES },
		{ label: $localize`:Visibility Condition Relation Excludes@@VisibilityConditionRelationExcludes:Does Not Include`, value: ConditionRelation.EXCLUDES },
		{ label: $localize`:Visibility Condition Relation Contained In@@VisibilityConditionRelationContainedIn:Contained In`, value: ConditionRelation.CONTAINED_IN },
		{ label: $localize`:Visibility Condition Relation Undefined@@VisibilityConditionRelationUndefined:Is Undefined`, value: ConditionRelation.UNDEFINED },
		{ label: $localize`:Visibility Condition Relation Not Undefined@@VisibilityConditionRelationNotUndefined:Is Not Undefined`, value: ConditionRelation.NOT_UNDEFINED },
	]

	// Add a new condition to the group
	addCondition(group: ConditionGroup, index: number) {
		const newCondition = { path: '', relation: ConditionRelation.EQ, value: '' };
		group.conditions.splice(index + 1, 0, newCondition);

		this.onChange.emit();
	}

	// Add a new nested group to the group
	addGroup(group: ConditionGroup, index: number) {
		const newGroup = { type: ConditionType.AND, conditions: [{ path: '', relation: ConditionRelation.EQ, value: '' }] };
		(group.conditions || []).splice(index + 1, 0, newGroup);

		this.onChange.emit();
	}

	addRootGroup() {
		this.group = { type: ConditionType.AND, conditions: [{ path: '', relation: ConditionRelation.EQ, value: '' }] };
		this.setRootGroup.emit(this.group);
	}

	// Remove a condition or group from the list
	removeConditionOrGroup(group: ConditionGroup, index: number) {
		console.log(group);
		group.conditions.splice(index, 1);

		this.onChange.emit();
	}

	remove(group: ConditionGroup) {
		console.log(group);
	}

	removeGroup() {
		if (this.parentGroup) {
			const index = this.parentGroup.conditions.indexOf(this.group);
			if (index !== -1) {
				this.parentGroup.conditions.splice(index, 1);
			}
		} else {
			this.group = null;
			this.setRootGroup.emit(null);
		}

		this.onChange.emit();
	}

	// Check if the item is a condition (not a group)
	isCondition(item: any): item is Condition {
		return 'path' in item;
	}

	update(conditionType: string, index: number, value: any) {
		if (this.isCondition(this.group.conditions[index])) {
			const currentCondition = this.group.conditions[index] as Condition
			if (conditionType === 'path') {
				currentCondition.path = value;
			}
			if (conditionType === 'relation') {
				currentCondition.relation = value;

				if (value === ConditionRelation.UNDEFINED || value === ConditionRelation.NOT_UNDEFINED) {
					currentCondition.value = undefined;
				} else {
					currentCondition.value = currentCondition.value  || '';
				}
			}
			if (conditionType === 'value') {
				currentCondition.value = value;
			}
		}

		this.onChange.emit();
	}

	updateGroupType(value: ConditionType) {
		this.group.type = value;

		this.onChange.emit();
	}

	isLastCondition(conditionOrGroup: ConditionGroup, index: number): boolean {
		const lastIndex = conditionOrGroup.conditions.length - 1;

		return lastIndex === index;
	}

	toggleCollapse() {
		this.collapse = !this.collapse;
	}

	countConditionsAndGroups(conditionGroup: ConditionGroup): string {
		let conditionCount = 0;
		let groupCount = 0;

		function traverse(group: ConditionGroup) {
			for (const conditionOrGroup of group.conditions) {
				if ('type' in conditionOrGroup) {
					groupCount++;
					traverse(conditionOrGroup);
				} else {
					conditionCount++;
				}
			}
		}

		traverse(conditionGroup);

		return `(Conditions: ${conditionCount}, Groups: ${groupCount})`;
	}

	countConditions(conditionOrGroup: ConditionGroup): number {
		// remove group from conditions
		const conditions = conditionOrGroup.conditions.filter(condition => !('type' in condition));
		return conditions.length;
	}
}
